/** Re-admission / supervisory cycle summary for the client Overview → Insurance card. */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { DueBadge, ScheduledInfo } from "./DuePanel";
import ScheduleSettingsDialog from "./ScheduleSettingsDialog";
import { fmtDate } from "./assessmentMeta";
import { Settings2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ruleLabel(item, settings, insurance) {
  if (item.kind === "supervisory") return `Every ${item.interval_days} days — agency rule`;
  const override = (settings?.readmission_by_insurance || []).find((r) => (r.insurance_company || "").trim().toLowerCase() === (insurance || "").trim().toLowerCase());
  return `Every ${item.interval_days} days — ${override ? `${override.insurance_company} rule` : "agency default"}`;
}

export default function AssessmentCycleInfo({ clientId, insuranceCompany }) {
  const { user } = useAuth();
  const isAdmin = (user?.role || "").toLowerCase() === "admin";
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/assessments/due`, { params: { client_id: clientId } }).then((r) => setData(r.data)).catch(() => setData({ items: [], settings: {} }));
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  if (!data) return null;
  const s = data.settings || {};
  const byKind = Object.fromEntries((data.items || []).map((i) => [i.kind, i]));
  const readm = byKind.readmission;
  const sup = byKind.supervisory;
  const fallbackReadmDays = (s.readmission_by_insurance || []).find((r) => (r.insurance_company || "").trim().toLowerCase() === (insuranceCompany || "").trim().toLowerCase())?.days || s.readmission_default_days;

  return (
    <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="assessment-cycle-info">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Assessment cycles</p>
        {isAdmin && (
          <button type="button" onClick={() => setEditing(true)} className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1" data-testid="cycle-edit-rules-btn">
            <Settings2 className="w-3 h-3" /> Edit rules
          </button>
        )}
      </div>
      <div className="grid sm:grid-cols-2 gap-3 text-sm">
        <div data-testid="cycle-readmission">
          <p className="info-label">Re-admission cycle</p>
          <p className="info-value">
            {readm ? ruleLabel(readm, s, insuranceCompany) : `Every ${fallbackReadmDays} days — ${fallbackReadmDays !== s.readmission_default_days ? `${insuranceCompany} rule` : "agency default"}`}
          </p>
          {readm ? (
            <p className="text-xs text-slate-500 mt-1 flex flex-wrap items-center gap-1.5">Next due {fmtDate(readm.due_date)} <DueBadge item={readm} /> <ScheduledInfo item={readm} /></p>
          ) : (
            <p className="text-xs text-slate-400 mt-1">Starts after the first completed Admission assessment.</p>
          )}
        </div>
        <div data-testid="cycle-supervisory">
          <p className="info-label">Supervisory visit cycle</p>
          <p className="info-value">Every {sup?.interval_days || s.supervisory_interval_days} days</p>
          {sup ? (
            <p className="text-xs text-slate-500 mt-1 flex flex-wrap items-center gap-1.5">Next due {fmtDate(sup.due_date)} <DueBadge item={sup} /> <ScheduledInfo item={sup} /></p>
          ) : (
            <p className="text-xs text-slate-400 mt-1">Tracked once the client is active.</p>
          )}
        </div>
      </div>
      <ScheduleSettingsDialog open={editing} onClose={() => setEditing(false)} onSaved={load} />
    </div>
  );
}
